import { MyMscExcursion, PortItem } from '@msc-b2c/interfaces/interfaces';
import { CruiseDay } from '@msc-b2c/interfaces/types';
import {
  getDynamicMediaPath,
  getFileNameWithoutExtension,
  getImageUrl,
} from '@msc-b2c/shared-utils';

/**
 * Converts an image URL to a Dynamic Media URL if not already using Dynamic Media.
 * Extracts asset code from AEM DAM paths and generates Dynamic Media URL.
 * Pattern matches the codebase standard (see imageUtils.ts).
 * @param imageUrl - The image URL (either imgDmPath or imgPath from data layer)
 * @returns Dynamic Media URL or original URL if conversion fails
 */
const ensureDynamicMediaUrl = (imageUrl: string): string => {
  if (!imageUrl) return '';

  // If already a Dynamic Media URL (contains 'is/image'), return as-is
  // This pattern is consistent with generateSrc/generateSrcSet in imageUtils.ts
  if (imageUrl.includes('is/image')) {
    return imageUrl;
  }

  // Extract asset code from AEM DAM path and convert to Dynamic Media
  const assetCode = getFileNameWithoutExtension(imageUrl);
  const dmUrl = getDynamicMediaPath(assetCode);

  // Return Dynamic Media URL if generated, otherwise fallback to original
  return dmUrl || imageUrl;
};

export const findFirstExcursionForDay = (day: CruiseDay, excursions: MyMscExcursion[]) => {
  // Find excursions that match the day (matching by portCode and date)
  const matchingExcursions = excursions.filter((excursion) => excursion.startDate === day.date);

  // Return the first matching excursion, or undefined if no match found
  return matchingExcursions.length > 0 ? matchingExcursions[0] : undefined;
};

export const resolveDayCardBackgroundImage = ({
  portCode,
  portsData,
  usedImagesByPortCode,
}: {
  portCode: string;
  portsData?: PortItem[] | null;
  usedImagesByPortCode: Map<string, Set<string>>;
}): string => {
  // Convert all port images to Dynamic Media URLs
  const portImages =
    portsData
      ?.filter((item) => item.portCode === portCode)
      .flatMap((item) => [item.desktopPortImage, item.sectionImage])
      .map((image) => {
        const imageUrl = getImageUrl(image);
        return ensureDynamicMediaUrl(imageUrl);
      })
      .filter(Boolean) || [];

  if (!usedImagesByPortCode.has(portCode)) {
    usedImagesByPortCode.set(portCode, new Set<string>());
  }

  const usedImagesForPort = usedImagesByPortCode.get(portCode) as Set<string>;
  let selected = portImages.find((image) => !usedImagesForPort.has(image));

  if (!selected) {
    usedImagesForPort.clear();
    selected = portImages[0];
  }

  if (selected) {
    usedImagesForPort.add(selected);
  }

  return selected || '';
};

/**
 * Resolves the background image for a day carousel card based on configuration.
 * Prioritizes port images when enabled; otherwise uses excursion images.
 * Both sources provide Dynamic Media URLs; fallback ensures coverage.
 * @param portImagesEnabled - If true, prioritize port images over excursion images
 * @param portImage - Background image from port rotation
 * @param excursionImage - Background image from first excursion of the day
 * @returns The selected background image URL
 */
export const resolveCardBackgroundImage = (
  portImagesEnabled: boolean,
  portImage: string,
  excursionImage: string
): string => {
  if (portImagesEnabled) {
    return portImage || excursionImage;
  }
  return excursionImage || portImage;
};
